import { useEffect, useState } from 'react';
import apiCoreAdapter from '@/hooks/apiCoreAdapter';
import { PsychologistFreeScheduleStatus } from '@/components/SessionDebug';
import { isFormacionReason, type FormacionBlockData } from '@/components/psychologist/calendar/FormacionBlockInfo';

const SLOT_MS = 30 * 60_000;

/**
 * Bloqueos de formación (motivo "Formación: …") dentro de un rango, ya agrupados: la API los
 * devuelve de a tramos de 30 min y acá se juntan los consecutivos con el mismo motivo en un
 * solo bloque. Lo usan la vista diaria y la mensual, que no cargan bloqueos por su cuenta
 * (la semanal ya los trae con el resto de la disponibilidad).
 */
export function useFormacionBlocks(from: Date, to: Date): FormacionBlockData[] {
  const [blocks, setBlocks] = useState<FormacionBlockData[]>([]);
  const fromMs = from.getTime();
  const toMs = to.getTime();

  useEffect(() => {
    let cancelled = false;
    apiCoreAdapter
      .psychologistAvailability({
        from: new Date(fromMs).toISOString(),
        to: new Date(toMs).toISOString(),
        source: 'psi_platform',
      })
      .then((slots) => {
        if (cancelled) return;
        const mine = slots
          .filter((s) => s.status !== PsychologistFreeScheduleStatus.AVAILABLE && isFormacionReason(s.reason))
          .map((s) => ({ reason: s.reason as string, startMs: new Date(s.slot).getTime() }))
          .filter((s) => !Number.isNaN(s.startMs))
          .sort((a, b) => a.startMs - b.startMs);

        const grouped: FormacionBlockData[] = [];
        let current: { reason: string; startMs: number; endMs: number } | null = null;
        const flush = () => {
          if (current) {
            grouped.push({
              reason: current.reason,
              startIso: new Date(current.startMs).toISOString(),
              endIso: new Date(current.endMs).toISOString(),
            });
          }
        };
        for (const s of mine) {
          // Un tramo ya cubierto (bloqueo duplicado) no extiende nada.
          if (current && current.reason === s.reason && s.startMs <= current.endMs) {
            current.endMs = Math.max(current.endMs, s.startMs + SLOT_MS);
            continue;
          }
          flush();
          current = { reason: s.reason, startMs: s.startMs, endMs: s.startMs + SLOT_MS };
        }
        flush();
        setBlocks(grouped);
      })
      .catch(() => { if (!cancelled) setBlocks([]); });
    return () => { cancelled = true; };
  }, [fromMs, toMs]);

  return blocks;
}
